import React, { useState } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Collapsible,
  Icon
} from '@shopify/polaris';
import { ChevronDownMinor } from '@shopify/polaris-icons';

// Q&A content kept in one place so the markup below stays a simple map. Answers
// describe the app's current behaviour: per-customer lifetime limits, login
// required for limited products, variant-aware limits, customer overrides, and
// the theme-embed + checkout-Function enforcement.
const FAQS = [
  {
    q: 'How are purchase limits counted?',
    a: 'Limits are per customer and count a customer’s entire purchase history for that product — a lifetime cap. For example, a limit of 2 means each customer can buy at most 2 in total, even across separate orders placed over time.'
  },
  {
    q: 'Can someone get round a limit with several accounts?',
    a: 'Turn on Apply limits per delivery address in Settings. Orders to the same address then share one limit. Collection orders are still limited per account.'
  },
  {
    q: 'Do customers need to be logged in?',
    a: 'To buy a product with a limit, yes. Otherwise, no. That’s how Order Guard reliably ties purchases to a person. A guest who tries to buy a limited product is prompted to sign in or create an account first.'
  },
  {
    q: 'Can I set different limits for different variants?',
    a: 'Yes. When you add a product limit (or a customer override) you can pick a specific variant, or leave it as “All variants” to cover the whole product. A limit on one variant only affects that variant — the product’s other variants are left unrestricted.'
  },
  {
    q: 'What’s the difference between Product Limits and Customer Overrides?',
    a: 'Product Limits set the default cap that applies to every customer. Customer Overrides are exceptions for one specific customer — you can raise or lower their limit, block them from a product entirely, or set an override that automatically expires on a date you choose.'
  },
  {
    q: 'What happens when a customer reaches their limit?',
    a: 'They’re stopped from adding any more of that product to their cart and shown a clear message explaining they’ve reached the limit. If they aren’t signed in, they’re prompted to log in first.'
  },
  {
    q: 'Does it work with Shop Pay and other express checkouts?',
    a: 'Yes. On limited products the express checkout buttons (Shop Pay, Apple Pay, and similar) are hidden until eligibility is confirmed, and a checkout-level safeguard also enforces the cap — so the limit can’t be skipped by going straight to express checkout.'
  }
];

function Help() {
  // Track which FAQs are expanded; each toggles independently.
  const [openFaqs, setOpenFaqs] = useState({});

  const toggleFaq = (index) => {
    setOpenFaqs((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <Page fullWidth title="Help & Support">
      <div className="og-help">
      <Layout>
        {/* FAQ Section */}
        <Layout.Section>
          <Card>
            <div style={{ padding: '16px' }}>
              <Text variant="headingLg" as="h2">
                Frequently Asked Questions
              </Text>
              <div style={{ marginTop: '8px' }}>
                {FAQS.map(({ q, a }, index) => {
                  const open = !!openFaqs[index];
                  return (
                    <div key={q} className="og-faq-item">
                      <button
                        type="button"
                        className="og-faq-trigger"
                        aria-expanded={open}
                        aria-controls={`og-faq-answer-${index}`}
                        onClick={() => toggleFaq(index)}
                      >
                        <Text variant="headingSm" as="span">{q}</Text>
                        <span className={`og-faq-chevron${open ? ' og-faq-chevron--open' : ''}`}>
                          <Icon source={ChevronDownMinor} />
                        </span>
                      </button>
                      <Collapsible
                        open={open}
                        id={`og-faq-answer-${index}`}
                        transition={{ duration: '150ms', timingFunction: 'ease' }}
                      >
                        <div className="og-faq-answer">
                          <Text as="p" color="subdued">{a}</Text>
                        </div>
                      </Collapsible>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        </Layout.Section>

        {/* Contact Information */}
        <Layout.Section>
          <Card>
            <div style={{ padding: '16px' }}>
              <Text variant="headingLg" as="h2">
                Need Help?
              </Text>
              <div style={{ marginTop: '16px' }}>
                <Text as="p">
                  For support, questions, or feedback, please contact us:
                </Text>
                <div style={{ marginTop: '12px' }}>
                  <Text as="p">
                    Email: <a href="mailto:support@maventech.dev" style={{ color: '#007ace', textDecoration: 'none' }}>
                      support@maventech.dev
                    </a>
                  </Text>
                </div>
                <div style={{ marginTop: '12px' }}>
                  <Text as="p" color="subdued">
                    We typically respond within 24 hours during business days.
                  </Text>
                </div>
              </div>
            </div>
          </Card>
        </Layout.Section>
      </Layout>
      </div>
    </Page>
  );
}

export default Help;
